/**
 * Factory Adapter — the bridge from creative direction to the Template
 * Factory, built WITHOUT modifying the factory.
 *
 * toFactoryPlan() converts a CreativeDirection into the exact inputs
 * `manufacture()` accepts today: a category plus gene `overrides`. Every
 * override value is validated against the factory's own exported enums,
 * so a factory vocabulary change surfaces here as a loud error instead of
 * a silently wrong template.
 *
 * Direction decisions the current pipeline cannot express yet (glass
 * panels, object overlap, diagonal cuts, …) are returned in
 * `futureEffects` — the documented extension points for the Design Engine.
 * Nothing is silently dropped.
 */

import {
  listCategories,
  listPaletteThemes,
  listTypographyThemes,
  listDecorationPacks,
  OFFER_STYLES,
  FOOTER_STYLES,
  IMAGE_STYLES,
  LAYOUT_POSITIONS,
  GENE_WEIGHTS,
  DEFAULT_SIMILARITY_THRESHOLD,
} from '../aiTemplateFactory';

/** Trend/composition effects the factory+engine can already express. */
const CONSUMABLE_EFFECTS = new Set(['decorationPack', 'typeScale', 'imageStyle']);

/**
 * The v1 Design Engine's balance metric measures the horizontal center of
 * mass against the canvas centerline, which caps asymmetric layouts
 * (split-hero scores ~12/100 on balance) below the 90 premium gate no
 * matter how good the design is. Until the engine's balance metric
 * understands intentional asymmetry, the adapter substitutes the nearest
 * symmetric layout and records the asymmetric intent as a future effect.
 */
const LAYOUT_FALLBACKS = { 'split-hero': 'classic-flyer' };

const assertOneOf = (value, allowed, label) => {
  if (!allowed.includes(value)) {
    throw new Error(`factoryAdapter: ${label} "${value}" is not in the factory vocabulary (${allowed.join(', ')})`);
  }
  return value;
};

const spacingFromNegativeSpace = (targetEmptyRatio) =>
  targetEmptyRatio >= 0.6 ? 'airy' : targetEmptyRatio >= 0.48 ? 'comfortable' : 'compact';

const decorationPackFallback = (style) => {
  if (style.layerDepth >= 0.6) return 'geo-pop';
  if (style.whitespaceBias >= 0.85) return 'clean';
  return 'soft-minimal';
};

/**
 * When direction pins genes, those genes are identical across the whole
 * batch by design — so the factory's flat 70% similarity rule would reject
 * everything after the first accepted template. Judge uniqueness on the
 * FREE genes instead: keep 70% of the variation space that remains.
 * threshold = pinnedWeight + (1 − pinnedWeight) × 0.7
 */
const similarityThresholdFor = (overrides) => {
  const pinnedWeight = Object.keys(overrides)
    .reduce((sum, gene) => sum + (GENE_WEIGHTS[gene] || 0), 0);
  return Number(
    (pinnedWeight + (1 - pinnedWeight) * DEFAULT_SIMILARITY_THRESHOLD).toFixed(4)
  );
};

/**
 * Convert a CreativeDirection into a factory plan.
 * Returns { category, overrides, minScore, futureEffects, rationale }.
 * Usage (today, zero factory changes):
 *   const plan = toFactoryPlan(direction);
 *   const { templates } = manufacture({ ...plan, count: 5 });
 */
export const toFactoryPlan = (direction, { minScore = 90 } = {}) => {
  const { audience, mood, brandStyle, trend, composition, negativeSpace, layoutReasoning } = direction;

  // Merge trend effects in selection order — first trend wins conflicts.
  const effects = {};
  const futureEffects = [];
  trend.selected.forEach(({ id, effects: fx }) => {
    Object.entries(fx).forEach(([key, value]) => {
      if (CONSUMABLE_EFFECTS.has(key)) {
        if (effects[key] === undefined) effects[key] = value;
      } else {
        futureEffects.push({ source: `trend:${id}`, effect: key, value });
      }
    });
  });
  if (composition.headingOverlapsHero) {
    futureEffects.push({ source: 'composition', effect: 'headingOverlapsHero', value: true });
  }
  if (composition.offerBreaksGrid) {
    futureEffects.push({ source: 'composition', effect: 'offerBreaksGrid', value: true });
  }
  if (composition.layerDepth >= 0.5) {
    futureEffects.push({ source: 'composition', effect: 'layerDepth', value: composition.layerDepth });
  }

  const typeScale = effects.typeScale
    ?? (brandStyle.typographyEmphasis >= 0.75 ? 'dramatic'
      : mood.sophistication >= 0.8 ? 'subtle' : 'balanced');

  let layout = layoutReasoning.recommendedLayout;
  if (LAYOUT_FALLBACKS[layout]) {
    futureEffects.push({ source: 'layoutReasoning', effect: 'asymmetricLayout', value: layout });
    layout = LAYOUT_FALLBACKS[layout];
  }

  const overrides = {
    layout: assertOneOf(layout, Object.keys(LAYOUT_POSITIONS), 'layout'),
    paletteTheme: assertOneOf(mood.paletteThemes[0], listPaletteThemes().map((t) => t.id), 'paletteTheme'),
    typographyTheme: assertOneOf(brandStyle.typographyTheme, listTypographyThemes().map((t) => t.id), 'typographyTheme'),
    typeScale,
    spacing: spacingFromNegativeSpace(negativeSpace.targetEmptyRatio),
    decorationPack: assertOneOf(
      effects.decorationPack ?? decorationPackFallback(brandStyle),
      listDecorationPacks().map((p) => p.id), 'decorationPack'
    ),
    offerStyle: assertOneOf(
      composition.offerEmphasis === 'dominant' ? 'price-circle'
        : composition.offerEmphasis === 'strong' ? 'ribbon-text' : 'badge',
      OFFER_STYLES, 'offerStyle'
    ),
    footerStyle: assertOneOf(
      composition.footerCompressed ? 'minimal'
        : mood.playfulness >= 0.5 ? 'social' : 'contact',
      FOOTER_STYLES, 'footerStyle'
    ),
    imageCount: composition.heroImportance === 'high'
      ? (composition.heroAreaRatio >= 0.45 ? 2 : 1)
      : composition.heroImportance === 'medium' ? 1 : 0,
    imageStyle: assertOneOf(
      effects.imageStyle ?? (mood.sophistication >= 0.8 ? 'studio' : 'photo'),
      IMAGE_STYLES, 'imageStyle'
    ),
  };

  return {
    category: assertOneOf(audience.factoryCategory, listCategories().map((c) => c.id), 'category'),
    overrides,
    minScore,
    similarityThreshold: similarityThresholdFor(overrides),
    futureEffects,
    rationale: layoutReasoning.reasoning,
  };
};
